import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner } from 'reactstrap';
import ExperienceShell from '../Experience/components/ExperienceShell';
import LocationStep from './components/LocationStep';
import DistributionStep from './components/DistributionStep';
import SpaceSummaryStep from './components/SpaceSummaryStep';
import AllSetStep from '../Experience/components/AllSetStep';
import { useSiteStore } from '../../stores/Assets/site';
import { useSpaceStore } from '../../stores/Assets/space';
import { useMenuStore } from '../../stores/Assets/menu';
import { useKitchenStore } from '../../stores/Assets/kitchen';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader } from 'lucide-react';
import { toast } from 'react-toastify';
import { useExperience } from '../../stores/Experience/Experience';
import { axiosHelper, tokenHelper } from '../../stores/helpers';

const SpaceManagementExperience = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const { getSites, sites } = useSiteStore();
    const { createSpace, getSpaces } = useSpaceStore();
    const { getMenus } = useMenuStore();
    const { getKitchens } = useKitchenStore();
    const { submitSpaces, loading: storeLoading, submissionProgress } = useExperience();
    const navigate = useNavigate();

    // Data passed between steps
    const [distributionData, setDistributionData] = useState({}); // { siteId: names[] }
    const [resultData, setResultData] = useState(null);
    const [preSelectedSiteId, setPreSelectedSiteId] = useState(null);

    useEffect(() => {
        const loadSites = async () => {
            setLoading(true);
            await getSites();
            setLoading(false);
        };
        loadSites();
    }, [getSites]);

    // Handle transitions
    const handleNext = async (data) => {
        if (currentStep === 1) {
            // Check if data is a valid ID (number or string, but not an event object)
            if (data && (typeof data === 'number' || typeof data === 'string')) {
                setPreSelectedSiteId(data);
            }
            setCurrentStep(2);
        } else if (currentStep === 2) {
            // Finished Distribution
            setDistributionData(data);
            await handleAutoFinalize(data);
        } else {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleAutoFinalize = async (distData) => {
        setFinalizing(true);
        try {
            const accessToken = await tokenHelper.getToken();
            const headers = { Authorization: `Bearer ${accessToken}` };

            // 1. Fetch all menus and kitchens for all sites to determine source and targets
            const siteDetails = await Promise.all(sites.map(async (site) => {
                const [menusRes, kitchensRes] = await Promise.all([
                    axiosHelper.get(`/menu/list/a?siteId=${site.site_id}&perPage=100`, { headers }),
                    axiosHelper.get(`/kitchen/site/${site.site_id}`, { headers })
                ]);
                return {
                    site_id: site.site_id,
                    name: site.name,
                    menus: menusRes?.menus || [],
                    kitchen_id: kitchensRes?.kitchens?.[0]?.kitchen_id || null
                };
            }));

            // 2. Find source site (first site with menus)
            const sourceSite = siteDetails.find(s => s.menus.length > 0);

            // 3. Prepare payload list
            const payloadList = [];

            for (const siteDetail of siteDetails) {
                const siteId = siteDetail.site_id;
                const newSpaces = distData[siteId] || [];
                const existingSpaces = await getSpaces(siteId, { page: 1, perPage: 100 }) || [];

                // 1. Determine or Duplicate Kitchen (Station)
                let targetKitchenId = siteDetail.kitchen_id;
                if (!targetKitchenId) {
                    const kRes = await axiosHelper.post('/kitchen/create', {
                        name: `${siteDetail.name}'s Station`,
                        site_id: siteId
                    }, { headers });
                    targetKitchenId = kRes?.kitchen?.kitchen_id;
                }

                // 2. Determine or Duplicate Menus
                let targetMenuIds = siteDetail.menus.map(m => m.menu_id);

                // If no menus, attempt to duplicate from sourceSite
                if (targetMenuIds.length === 0 && sourceSite) {
                    toast.info(`Setting up menus for ${siteDetail.name}...`, { autoClose: 2000 });
                    for (const sourceMenu of sourceSite.menus) {
                        const newName = `${siteDetail.name}'s ${sourceMenu.name || sourceMenu.title}`;
                        const dupRes = await axiosHelper.post(`/menu/duplicate/${sourceMenu.menu_id}`, {
                            site_id: siteId,
                            name: newName
                        }, { headers });
                        if (dupRes?.menu_id) targetMenuIds.push(dupRes.menu_id);
                    }
                }

                // FINAL FALLBACK: If still no menus (e.g. sourceSite also had none), create a default one
                if (targetMenuIds.length === 0) {
                    const fallbackMenu = await axiosHelper.post('/menu/create', {
                        name: 'Main Menu',
                        site_id: siteId,
                        currency: 'QAR'
                    }, { headers });
                    if (fallbackMenu?.menu_id) targetMenuIds.push(fallbackMenu.menu_id);
                }

                // Create entries for NEW spaces
                newSpaces.forEach(name => {
                    payloadList.push({
                        siteId,
                        name,
                        kitchenId: targetKitchenId,
                        menuIds: targetMenuIds,
                        isExisting: false
                    });
                });

                // Create entries for EXISTING spaces (to link them to menus)
                existingSpaces.forEach(space => {
                    payloadList.push({
                        siteId,
                        name: space.name,
                        spaceId: space.space_id,
                        kitchenId: targetKitchenId,
                        menuIds: targetMenuIds,
                        isExisting: true
                    });
                });
            }

            if (payloadList.length === 0) {
                toast.warning("No spaces to process.");
                setFinalizing(false);
                return;
            }

            // 4. Submit
            await submitSpaces(payloadList);
            setCurrentStep(3); // Now SpaceSummaryStep is at index 2 (step 3)
        } catch (error) {
            console.error("Auto Finalization failed", error);
            toast.error("Process failed. Please try again.");
        } finally {
            setFinalizing(false);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleFinalize = async (payloadList) => {
        setFinalizing(true);
        try {
            await submitSpaces(payloadList);
            // Success! Move to summary
            setCurrentStep(4);
        } catch (error) {
            console.error("Finalization failed", error);
            toast.error("Failed to create spaces. Please try again.");
        } finally {
            setFinalizing(false);
        }
    };

    if (finalizing) {
        return (
            <div className="vh-100 d-flex flex-column align-items-center justify-content-center bg-white">
                <div className="text-center">
                    <div className="mb-4 d-flex justify-content-center">
                        <div className="p-4 rounded-circle bg-light">
                            <Spinner style={{ width: '3rem', height: '3rem', color: '#000' }} />
                        </div>
                    </div>
                    <h3 className="fw-bold mb-2">Finalizing Your Setup...</h3>
                    <p className="text-muted">{submissionProgress || 'Generating QR codes and linking your menus.'}</p>
                </div>
            </div>
        );
    }

    const steps = [
        { name: 'Locations', component: LocationStep },
        { name: 'Distribution', component: DistributionStep },
        { name: 'Complete', component: SpaceSummaryStep }
    ];

    const ActiveComponent = steps[currentStep - 1].component;

    // Determine props based on step
    let stepProps = {
        sites,
        onNext: handleNext,
        onBack: handleBack,
    };

    if (currentStep === 2) {
        stepProps = {
            ...stepProps,
            initialSiteId: preSelectedSiteId,
            distributionData,
            setDistributionData
        };
    }



    // Hide footer for Step 2 and 3 as they have internal navigation
    const hideFooter = currentStep === 2 || currentStep === 3;

    return (
        <div className="bg-light min-vh-100 h-100 d-flex flex-column">
            {currentStep === 3 ? (
                <SpaceSummaryStep sites={sites} onBack={handleBack} />
            ) : (
                <ExperienceShell
                    currentStep={currentStep}
                    totalSteps={2}
                    onBack={currentStep > 1 ? handleBack : undefined}
                    onNext={handleNext}
                    hideFooter={hideFooter}
                    nextLabel='Continue'
                    showMobilePreview={false}
                >
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center h-100 py-5">
                            <Spinner color="primary" />
                        </div>
                    ) : (
                        <ActiveComponent {...stepProps} />
                    )}
                </ExperienceShell>
            )}
        </div>
    );
};

export default SpaceManagementExperience;
